import { inject, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import type { InjectionKey, Ref } from 'vue';
import { DraftConflictError, DraftRequestError } from './host';
import type { DraftService, DraftState } from './host';
import type { BuilderStore } from './useBuilderStore';

/** Distinct publish failure kinds the bar can explain to the user. */
export type PublishError = 'forbidden' | 'missing' | 'save_failed' | 'server';

/**
 * Working-copy lifecycle for the shell: load/create on mount, autosave
 * (2 s debounce, 10 s max interval, flush on tab hide), 30 s lock
 * heartbeat, publish / discard / takeover. Transport lives in the host's
 * DraftService; this is the pure state machine.
 */
export interface DraftAutosave {
    status: Ref<'loading' | 'idle' | 'saving' | 'saved' | 'error' | 'readonly'>;
    savedAt: Ref<string | null>;
    lockUser: Ref<string | null>;
    conflict: Ref<boolean>;
    /** Working copy differs from the published content (discard has effect). */
    changed: Ref<boolean>;
    /** Publish request in flight — the bar disables the button on it. */
    publishing: Ref<boolean>;
    /** Transient success flag; auto-clears ~2.5 s after a publish lands. */
    published: Ref<boolean>;
    /** Last publish failure kind; null when none (dismiss resets to null). */
    publishError: Ref<PublishError | null>;
    publish(force?: boolean): Promise<boolean>;
    discard(): Promise<void>;
    takeover(): Promise<void>;
}

const DEBOUNCE_MS = 2_000;
const MAX_INTERVAL_MS = 10_000;
const HEARTBEAT_MS = 30_000;
const PUBLISHED_FLASH_MS = 2_500;

const AUTOSAVE_KEY: InjectionKey<DraftAutosave> = Symbol('builder-v2-autosave');

export function injectDraftAutosave(): DraftAutosave {
    const autosave = inject(AUTOSAVE_KEY);

    if (!autosave) {
        throw new Error('injectDraftAutosave must be used inside a BuilderV2 tree');
    }

    return autosave;
}

/** Canvas-lite has no working copy — draft-aware chrome degrades on null. */
export function injectDraftAutosaveOptional(): DraftAutosave | null {
    return inject(AUTOSAVE_KEY, null);
}

/**
 * Publish flow shared by the builder bar and the page context menu — the
 * canvas surface passes reload so the preview re-renders published content.
 *
 * @param autosave Draft state machine.
 * @param reload   Canvas reload; null on the document surface.
 * @return void No return expected.
 */
export async function publishDraft(autosave: DraftAutosave, reload: (() => void) | null): Promise<void> {
    if (await autosave.publish()) {
        reload?.();
    }
}

/**
 * Discard flow shared by the builder bar and the page context menu.
 *
 * @param autosave Draft state machine.
 * @param confirm  Resolves the user's decision (dialog or window.confirm).
 * @param reload   Canvas reload; null on the document surface.
 * @return void No return expected.
 */
export async function discardDraft(
    autosave: DraftAutosave,
    confirm: () => Promise<boolean>,
    reload: (() => void) | null,
): Promise<void> {
    if (!(await confirm())) {
        return;
    }

    await autosave.discard();
    reload?.();
}

export function useDraftAutosave(store: BuilderStore, draft: DraftService): DraftAutosave {
    const status = ref<DraftAutosave['status']['value']>('loading');
    const savedAt = ref<string | null>(null);
    const lockUser = ref<string | null>(null);
    const conflict = ref(false);
    const changed = ref(true);
    const publishing = ref(false);
    const published = ref(false);
    const publishError = ref<PublishError | null>(null);

    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    let oldestPending = 0;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let publishedTimer: ReturnType<typeof setTimeout> | null = null;

    const applyDraftResponse = (data: DraftState): void => {
        store.content.value = Array.isArray(data.content) ? data.content : [];
        store.mode.value = data.mode;
        lockUser.value = data.lock.user;
        savedAt.value = data.updated_at;
        // Permissive fallback: an older backend without the flag never dims discard.
        changed.value = data.changed ?? true;
        status.value = data.mode === 'readonly' ? 'readonly' : 'idle';
    };

    const load = async (takeover = false): Promise<void> => {
        try {
            applyDraftResponse(await draft.show(takeover ? { takeover: true } : {}));
        } catch {
            status.value = 'error';
        }
    };

    const save = async (): Promise<void> => {
        if (store.mode.value === 'readonly' || !store.dirty.value) {
            return;
        }

        status.value = 'saving';

        try {
            const { saved_at } = await draft.save(store.content.value);
            store.dirty.value = false;
            savedAt.value = saved_at;
            status.value = 'saved';
        } catch (error) {
            status.value = error instanceof DraftConflictError ? 'readonly' : 'error';
        }
    };

    const schedule = (): void => {
        const now = Date.now();
        oldestPending = oldestPending || now;

        if (saveTimer) {
            clearTimeout(saveTimer);
        }

        const delay = now - oldestPending >= MAX_INTERVAL_MS ? 0 : DEBOUNCE_MS;

        saveTimer = setTimeout(() => {
            oldestPending = 0;
            void save();
        }, delay);
    };

    const flashPublished = (): void => {
        published.value = true;

        if (publishedTimer) {
            clearTimeout(publishedTimer);
        }

        publishedTimer = setTimeout(() => (published.value = false), PUBLISHED_FLASH_MS);
    };

    const publishFailure = (error: unknown): PublishError => {
        if (error instanceof DraftRequestError && error.status === 403) {
            return 'forbidden';
        }

        if (error instanceof DraftRequestError && error.status === 404) {
            return 'missing';
        }

        return 'server';
    };

    watch(store.lastMutation, (mutation) => {
        if (mutation !== null) {
            changed.value = true;
            schedule();
        }
    });

    /** Synchronous best-effort flush when the tab hides or unloads. */
    const flush = (): void => {
        if (store.mode.value === 'readonly' || !store.dirty.value) {
            return;
        }

        draft.saveBeacon(store.content.value);
        store.dirty.value = false;
    };

    const onVisibilityChange = (): void => {
        if (document.visibilityState === 'hidden') {
            flush();
        }
    };

    onMounted(() => {
        void load();

        heartbeatTimer = setInterval(() => {
            if (store.mode.value !== 'edit') {
                return;
            }

            void draft.heartbeat().catch(() => (status.value = 'readonly'));
        }, HEARTBEAT_MS);

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('beforeunload', flush);
    });

    onBeforeUnmount(() => {
        if (saveTimer) {
            clearTimeout(saveTimer);
        }

        if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
        }

        if (publishedTimer) {
            clearTimeout(publishedTimer);
        }

        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('beforeunload', flush);
        flush();
    });

    const autosave: DraftAutosave = {
        status,
        savedAt,
        lockUser,
        conflict,
        changed,
        publishing,
        published,
        publishError,
        async publish(force = false) {
            if (publishing.value) {
                return false;
            }

            publishing.value = true;
            published.value = false;
            publishError.value = null;

            try {
                await save();

                if (status.value === 'error') {
                    // Publishing now would ship a draft missing the unsaved edits.
                    publishError.value = 'save_failed';

                    return false;
                }

                if (status.value === 'readonly') {
                    return false;
                }

                conflict.value = false;
                await draft.publish(force);
                store.dirty.value = false;
                status.value = 'idle';
                await load();
                flashPublished();

                return true;
            } catch (error) {
                if (error instanceof DraftConflictError) {
                    conflict.value = true;
                } else {
                    status.value = 'error';
                    publishError.value = publishFailure(error);
                }

                return false;
            } finally {
                publishing.value = false;
            }
        },
        async discard() {
            await draft.discard();
            store.dirty.value = false;
            await load();
        },
        async takeover() {
            await load(true);
        },
    };

    provide(AUTOSAVE_KEY, autosave);

    return autosave;
}
